import { useAuthStore } from '@/stores/authStore'

const DEMO_API_BASE = '/demo-api'

/**
 * Generic fetch wrapper for the demo API (adds x-bytebeam-tenant for project-scoped calls)
 */
async function demoApiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
	const currentTenant = useAuthStore.getState().currentTenant

	const res = await fetch(`${DEMO_API_BASE}${path}`, {
		...options,
		headers: {
			'Content-Type': 'application/json',
			'x-bytebeam-tenant': currentTenant,
			...options.headers,
		},
	})

	if (!res.ok) {
		const error = await res.json().catch(() => ({ error: res.statusText }))
		throw new Error(error.error || `Demo API error: ${res.status}`)
	}

	return res.json()
}

async function demoApiPostBlob(path: string, body: unknown): Promise<Blob> {
	const currentTenant = useAuthStore.getState().currentTenant
	const res = await fetch(`${DEMO_API_BASE}${path}`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json', 'x-bytebeam-tenant': currentTenant },
		body: JSON.stringify(body),
	})
	if (!res.ok) {
		const error = (await res.json().catch(() => null)) as { error_message?: unknown; error?: unknown } | null
		const message = error?.error_message ?? error?.error
		throw new Error(typeof message === 'string' ? message : `Demo API error: ${res.status}`)
	}
	return res.blob()
}

/**
 * Fetch for organization-level demo API calls (no x-bytebeam-tenant header)
 */
async function demoApiFetchNoTenant<T>(path: string, options: RequestInit = {}): Promise<T> {
	const res = await fetch(`${DEMO_API_BASE}${path}`, {
		...options,
		headers: {
			'Content-Type': 'application/json',
			...options.headers,
		},
	})

	if (!res.ok) {
		const error = await res.json().catch(() => ({ error: res.statusText }))
		throw new Error(error.error || `Demo API error: ${res.status}`)
	}

	return res.json()
}

export const demoApi = {
	get: <T>(path: string) => demoApiFetch<T>(path),
	/** GET without x-bytebeam-tenant (for organization-level endpoints) */
	getNoTenant: <T>(path: string) => demoApiFetchNoTenant<T>(path),
	/** GET with x-bytebeam-tenant header (for per-tenant device lists) */
	getWithTenantId: <T>(path: string, tenantId: string) =>
		demoApiFetchNoTenant<T>(path, {
			headers: { 'x-bytebeam-tenant': tenantId },
		}),
	/** POST without x-bytebeam-tenant (for organization-level endpoints) */
	postNoTenant: <T>(path: string, body?: unknown) =>
		demoApiFetchNoTenant<T>(path, {
			method: 'POST',
			body: body ? JSON.stringify(body) : undefined,
		}),
	/** DELETE without x-bytebeam-tenant (for organization-level endpoints) */
	deleteNoTenant: <T>(path: string, body?: unknown) =>
		demoApiFetchNoTenant<T>(path, {
			method: 'DELETE',
			body: body ? JSON.stringify(body) : undefined,
		}),
	/** PUT without x-bytebeam-tenant (for /me and other user-level endpoints) */
	putNoTenant: <T>(path: string, body?: unknown) =>
		demoApiFetchNoTenant<T>(path, {
			method: 'PUT',
			body: body ? JSON.stringify(body) : undefined,
		}),
	/** PATCH without x-bytebeam-tenant (for org-level device operations) */
	patchNoTenant: <T>(path: string, body?: unknown) =>
		demoApiFetchNoTenant<T>(path, {
			method: 'PATCH',
			body: body ? JSON.stringify(body) : undefined,
		}),
	post: <T>(path: string, body?: unknown, options: RequestInit = {}) =>
		demoApiFetch<T>(path, {
			...options,
			method: 'POST',
			body: body ? JSON.stringify(body) : undefined,
		}),
	postBlob: (path: string, body: unknown) => demoApiPostBlob(path, body),
	put: <T>(path: string, body?: unknown, options: RequestInit = {}) =>
		demoApiFetch<T>(path, {
			...options,
			method: 'PUT',
			body: body ? JSON.stringify(body) : undefined,
		}),
	patch: <T>(path: string, body?: unknown, options: RequestInit = {}) =>
		demoApiFetch<T>(path, {
			...options,
			method: 'PATCH',
			body: body ? JSON.stringify(body) : undefined,
		}),
	delete: <T>(path: string, body?: unknown, options: RequestInit = {}) =>
		demoApiFetch<T>(path, {
			...options,
			method: 'DELETE',
			body: body ? JSON.stringify(body) : undefined,
		}),
}
